import React from "react";
import { Branch } from "../Branch";

import { NewBranch as GenericBranch } from "~/Branch";
import { Company } from "~/Company";
import * as Driver from "~/Driver";
import { Order } from "~/Order";

export function Name(datum: Driver.Table.Datum) {
  return (
    <span data-testid="driver-name">
      <Driver.Name.Component {...datum} includeInactive />
      {
        <Driver.Table.Detail>
          {datum.branch?.branch_id ? (
            <Branch.Name branch={datum.branch} />
          ) : datum.homeOffice ? (
            <Company.Location
              {...datum.homeOffice}
              labelPrefix="Driver's work reporting location:"
            />
          ) : (
            <GenericBranch.BranchName {...datum} />
          )}
        </Driver.Table.Detail>
      }
    </span>
  );
}

export namespace Name {
  export const column = (filter: Driver.Table.Filter) => ({
    ...Settings.basics(),
    ...Settings.filtering(filter),
    ...Settings.sorting(),
    ...Settings.rendering(),
  });

  namespace Settings {
    export const basics = () => ({
      title: "Name",
      dataIndex: "name",
    });

    export const filtering = (filter: Driver.Table.Filter) => ({
      onFilter: (_: unknown, { id }: Driver.Driver) => {
        const f = filter.filter;

        const driverFilterEnabled = !!f?.drivers?.length;
        const driverFilterMatch =
          driverFilterEnabled && f?.drivers?.map((d) => d.id).includes(id);

        if (driverFilterEnabled && !driverFilterMatch) return false;

        return true;
      },
      filteredValue: Filter.isActive(filter) ? ["filtered"] : [],
      filterDropdown: ({
        clearFilters: clear,
        confirm,
      }: {
        readonly confirm: () => void;
        readonly clearFilters?: () => void;
      }) => <Filter {...filter} clear={clear} confirm={confirm} />,
    });

    export const sorting = () => ({
      sorter: (a: Driver.Table.Datum, b: Driver.Table.Datum) =>
        Order.optionally(Driver.Name.compare)(a.name, b.name),
    });

    export const rendering = () => ({
      render: (_: never, datum: Driver.Table.Datum) => <Name {...datum} />,
    });
  }

  export function Filter({
    filter,
    clear,
    confirm,
  }: Driver.Table.Filter.Props) {
    const controls = {
      confirm,
      ...(Filter.isActive({ filter }) && {
        clear: () => {
          clear?.();
          pipe({ filter }, Driver.Table.Filter.Update.drivers(), filter.update);
        },
      }),
    };

    const drivers = (
      <Driver.Table.Filter.Section>
        <Driver.Drivers.Select
          placeholder="Filter by driver"
          bordered={false}
          style={Driver.Table.Filter.style()}
          selected={filter}
          select={(drivers) =>
            filter.update(
              Driver.Table.Filter.Update.drivers(drivers)({ filter })
            )
          }
          clear={() =>
            filter.update(Driver.Table.Filter.Update.drivers()({ filter }))
          }
        />
      </Driver.Table.Filter.Section>
    );

    return (
      <Driver.Table.Filter filter={filter} {...controls}>
        {drivers}
      </Driver.Table.Filter>
    );
  }

  export namespace Filter {
    export const isActive = ({ filter }: Driver.Table.Filter) =>
      [...(filter.drivers ?? [])].length > 0;
  }
}
